import { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { OfflineProvider } from '@/contexts/OfflineContext';
import { AppLockProvider } from '@/contexts/AppLockContext';
import { DemoProvider } from '@/contexts/DemoContext';
import { EntitlementProvider } from '@/contexts/EntitlementContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import DemoProtectedRoute from '@/components/DemoProtectedRoute';
import OnboardingWrapper from '@/components/onboarding/OnboardingWrapper';
import AppLayout from '@/components/AppLayout';
import OfflineIndicator from '@/components/OfflineIndicator';
import ReminderNotificationCenter from '@/components/ReminderNotificationCenter';
import AppLockGate from '@/components/AppLockGate';
import NativeAppService from '@/services/nativeAppService';
import '@/App.css';

// Public pages
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage/VerifyEmailPage';
import TwoFactorVerifyPage from '@/pages/TwoFactorVerifyPage/TwoFactorVerifyPage';
import PrivacyPolicyPage from '@/pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/legal/TermsOfServicePage';
import AuthCallback from '@/pages/AuthCallback';

// Protected pages
import HomePage from '@/pages/HomePage';
import SearchPage from '@/pages/SearchPage';
import CalendarPage from '@/pages/CalendarPage';
import CarMaintenancePage from '@/pages/car-maintenance/CarMaintenancePage';
import MedicalPage from '@/pages/medical/MedicalPage';
import MedicalDashboardPage from '@/pages/medical/MedicalDashboardPage';
import AppointmentPrepPage from '@/pages/medical/AppointmentPrepPage';
import MedicalDoctorSummaryPage from '@/pages/medical/DoctorSummaryPage';
import SymptomsPage from '@/pages/medical/SymptomsPage';
import QuestionsPage from '@/pages/medical/QuestionsPage';
import AppointmentDetailPage from '@/pages/medical/AppointmentDetailPage';
import VisitNotesPage from '@/pages/medical/VisitNotesPage';
import AppointmentsListPage from '@/pages/medical/AppointmentsListPage';
import DatesPage from '@/pages/dates/DatesPage';
import DocumentsPage from '@/pages/documents/DocumentsPage';
import DocumentDetailPage from '@/pages/documents/DocumentDetailPage';
import ParkingPage from '@/pages/parking/ParkingPage';
import ShoppingPage from '@/pages/shopping/ShoppingPage';
import ContactsPlacesPage from '@/pages/contacts-places/ContactsPlacesPage';
import NotificationSettingsPage from '@/pages/NotificationSettingsPage';
import CalendarSettingsPage from '@/pages/CalendarSettingsPage';
import WidgetSettingsPage from '@/pages/settings/WidgetSettingsPage';
import SecurityAuditPage from '@/pages/admin/SecurityAuditPage';
import EmergencyBinderPage from '@/pages/emergency-binder/EmergencyBinderPage';
import MedicalInfoPage from '@/pages/emergency-binder/MedicalInfoPage';
import EmergencyContactsPage from '@/pages/emergency-binder/EmergencyContactsPage';
import BinderDocumentsPage from '@/pages/emergency-binder/DocumentsPage';
import HouseholdInfoPage from '@/pages/emergency-binder/HouseholdInfoPage';
import EmergencyModePage from '@/pages/emergency-binder/EmergencyModePage';
import DoctorSummaryPage from '@/pages/emergency-binder/DoctorSummaryPage';
import LockScreenCardPage from '@/pages/emergency-binder/LockScreenCardPage';
import PrintBinderPage from '@/pages/emergency-binder/PrintBinderPage';
import FinancialOverviewPage from '@/pages/emergency-binder/FinancialOverviewPage';
import EndOfLifePage from '@/pages/emergency-binder/EndOfLifePage';
import FamilySharingPage from '@/pages/emergency-binder/FamilySharingPage';
import EmergencySummaryPage from '@/pages/emergency-binder/EmergencySummaryPage';
import EmergencyViewPage from '@/pages/emergency-binder/EmergencyViewPage';
import FamilyNetworkPage from '@/pages/emergency-binder/FamilyNetworkPage';
import CaregiverPage from '@/pages/emergency-binder/CaregiverPage';
import PetInfoPage from '@/pages/PetInfoPage';
import SubscriptionPlansPage from '@/pages/SubscriptionPlansPage';
import AccountSettingsPage from '@/pages/AccountSettingsPage';

/**
 * ProtectedPageWithNav - Wraps protected pages with navigation
 * Supports both real auth and demo mode
 */
const ProtectedPageWithNav = ({ children }) => (
  <DemoProtectedRoute>
    <OnboardingWrapper>
      <AppLayout>
        {children}
      </AppLayout>
    </OnboardingWrapper>
  </DemoProtectedRoute>
);

/**
 * ProtectedPageNoNav - Protected page without bottom nav (for settings, etc.)
 * Supports both real auth and demo mode
 */
const ProtectedPageNoNav = ({ children }) => (
  <DemoProtectedRoute>
    <AppLayout hideNav>
      {children}
    </AppLayout>
  </DemoProtectedRoute>
);

function App() {
  // Initialize native app features on mount
  useEffect(() => {
    NativeAppService.initialize();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <DemoProvider>
          <AuthProvider>
            <EntitlementProvider>
              <AppLockProvider>
                <OfflineProvider>
                  <AppLockGate>
                  <Routes>
                  {/* Public routes - no bottom nav */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/2fa-verify" element={<TwoFactorVerifyPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  
                  {/* Subscription plans page - accessible to all (demo/auth) */}
                  <Route path="/subscription/plans" element={
                    <DemoProtectedRoute>
                      <SubscriptionPlansPage />
                    </DemoProtectedRoute>
                  } />
                  
                  {/* Public emergency view - for QR code access without login */}
                  <Route path="/emergency-view/:accessToken" element={<EmergencyViewPage />} />
            
            {/* Protected routes with bottom navigation */}
            <Route path="/" element={
              <ProtectedPageWithNav>
                <HomePage />
              </ProtectedPageWithNav>
            } />
            <Route path="/search" element={
              <ProtectedPageWithNav>
                <SearchPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/calendar" element={
              <ProtectedPageWithNav>
                <CalendarPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/car-maintenance" element={
              <ProtectedPageWithNav>
                <CarMaintenancePage />
              </ProtectedPageWithNav>
            } />
            <Route path="/medical" element={
              <ProtectedPageWithNav>
                <MedicalDashboardPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/medical/records" element={
              <ProtectedPageWithNav>
                <MedicalPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/medical/prepare" element={
              <ProtectedPageNoNav>
                <AppointmentPrepPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/summary" element={
              <ProtectedPageNoNav>
                <MedicalDoctorSummaryPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/symptoms" element={
              <ProtectedPageNoNav>
                <SymptomsPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/questions" element={
              <ProtectedPageNoNav>
                <QuestionsPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/appointments" element={
              <ProtectedPageNoNav>
                <AppointmentsListPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/appointment/:appointmentId" element={
              <ProtectedPageNoNav>
                <AppointmentDetailPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/medical/appointment/:appointmentId/complete" element={
              <ProtectedPageNoNav>
                <VisitNotesPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/dates" element={
              <ProtectedPageWithNav>
                <DatesPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/documents" element={
              <ProtectedPageWithNav>
                <DocumentsPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/documents/:documentId" element={
              <ProtectedPageWithNav>
                <DocumentDetailPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/parking" element={
              <ProtectedPageWithNav>
                <ParkingPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/shopping" element={
              <ProtectedPageWithNav>
                <ShoppingPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/contacts-places" element={
              <ProtectedPageWithNav>
                <ContactsPlacesPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/pet-info" element={
              <ProtectedPageWithNav>
                <PetInfoPage />
              </ProtectedPageWithNav>
            } />
            
            {/* Emergency Binder */}
            <Route path="/emergency-binder" element={
              <ProtectedPageWithNav>
                <EmergencyBinderPage />
              </ProtectedPageWithNav>
            } />
            <Route path="/emergency-binder/:profileId/medical" element={
              <ProtectedPageNoNav>
                <MedicalInfoPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/contacts" element={
              <ProtectedPageNoNav>
                <EmergencyContactsPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/documents" element={
              <ProtectedPageNoNav>
                <BinderDocumentsPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/household" element={
              <ProtectedPageNoNav>
                <HouseholdInfoPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/emergency-mode" element={
              <ProtectedPageNoNav>
                <EmergencyModePage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/doctor-summary" element={
              <ProtectedPageNoNav>
                <DoctorSummaryPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/lock-screen-card" element={
              <ProtectedPageNoNav>
                <LockScreenCardPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/print" element={
              <ProtectedPageNoNav>
                <PrintBinderPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/financial" element={
              <ProtectedPageNoNav>
                <FinancialOverviewPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/endoflife" element={
              <ProtectedPageNoNav>
                <EndOfLifePage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/sharing" element={
              <ProtectedPageNoNav>
                <FamilySharingPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/:profileId/summary" element={
              <ProtectedPageNoNav>
                <EmergencySummaryPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/family-network" element={
              <ProtectedPageNoNav>
                <FamilyNetworkPage />
              </ProtectedPageNoNav>
            } />
            <Route path="/emergency-binder/caregivers" element={
              <ProtectedPageNoNav>
                <CaregiverPage />
              </ProtectedPageNoNav>
            } />
            
            {/* Settings pages - no bottom nav for cleaner UX */}
            <Route path="/settings/notifications" element={
              <ProtectedPageNoNav>
                <NotificationSettingsPage />
              </ProtectedPageNoNav>
            } />
            
            <Route path="/settings/calendar" element={
              <ProtectedPageNoNav>
                <CalendarSettingsPage />
              </ProtectedPageNoNav>
            } />
            
            <Route path="/settings/widgets" element={
              <ProtectedPageNoNav>
                <WidgetSettingsPage />
              </ProtectedPageNoNav>
            } />
            
            <Route path="/settings/account" element={
              <ProtectedPageNoNav>
                <AccountSettingsPage />
              </ProtectedPageNoNav>
            } />
            
            {/* Admin pages */}
            <Route path="/admin/security" element={
              <ProtectedPageNoNav>
                <SecurityAuditPage />
              </ProtectedPageNoNav>
            } />
            </Routes>
                <OfflineIndicator />
                <ReminderNotificationCenter />
              </AppLockGate>
            </OfflineProvider>
          </AppLockProvider>
        </EntitlementProvider>
        </AuthProvider>
        </DemoProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
